import type { ConfiguredFieldValuesInput } from '@cims-online/shared'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useMemo, useState } from 'react'
import type { ConfiguredFieldsSession } from '../../utils/configuredFields/types'
import { resolveConfiguredFieldsFormDefaults } from '../../utils/configuredFields/utils'
import type { ConfiguredFieldsController } from './types'
import { validateConfiguredFields } from './validation'

/**
 * Manages configured fields beneath a PayItemSelectProvider.
 *
 * Loaded project pay items rebuild unedited defaults; user edits always win.
 * Errors appear after blur or an explicit revealErrors call from the owning
 * form. Remount for each field session to reset edits and error visibility.
 */
export function useConfiguredFields(
  session: ConfiguredFieldsSession
): ConfiguredFieldsController {
  const fields = session.context.fields
  const { loading: payItemsLoading, payItems: loadedPayItems } =
    usePayItemSelectContext()
  const payItemsReady =
    !payItemsLoading || !fields.some(({ type }) => type === 'pay_items')
  const defaults = useMemo(
    () =>
      resolveConfiguredFieldsFormDefaults(
        session,
        payItemsReady ? loadedPayItems : undefined
      ),
    [loadedPayItems, payItemsReady, session]
  )
  const [editedValues, setEditedValues] = useState<ConfiguredFieldValuesInput>(
    {}
  )
  const values = useMemo(
    () => ({ ...defaults.initialValues, ...editedValues }),
    [defaults.initialValues, editedValues]
  )
  const validation = useMemo(
    () =>
      validateConfiguredFields(fields, values, defaults.fieldValueContexts, {
        approvalFields: session.context.approvalFields,
        deletedPayItemIds: defaults.deletedPayItemIds,
      }),
    [
      defaults.deletedPayItemIds,
      defaults.fieldValueContexts,
      fields,
      session.context.approvalFields,
      values,
    ]
  )
  const [touched, setTouched] = useState(() => new Set<string>())
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const visibleFieldIssues = Object.fromEntries(
    Object.entries(validation.fieldIssues).filter(
      ([fieldId]) => submitAttempted || touched.has(fieldId)
    )
  )

  return {
    loading: !payItemsReady,
    onBlur: (fieldId) => {
      setTouched((keys) => new Set(keys).add(fieldId))
    },
    onChange: (fieldId, value) => {
      setEditedValues((currentValues) => ({
        ...currentValues,
        [fieldId]: value,
      }))
    },
    payItems: defaults.payItems,
    revealErrors: () => setSubmitAttempted(true),
    validation,
    values,
    visibleFieldIssues,
  }
}
